import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AttentionRow,
  Box,
  Button,
  EmptyState,
  Screen,
  Text,
} from '~/atomic';
import { useAppDispatch, useAppSelector } from '~/store';
import {
  selectAttentionAll,
  selectOverdueAttention,
  type AttentionItem,
} from '~/store/contracts/selectors';
import { fetchContracts } from '~/store/contracts/thunks';
import { formatCentsToBRLCompact } from '~/utils/formatFinance';

type FilterKey = 'all' | 'overdue' | 'soon';

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'Tudo' },
  { key: 'overdue', label: 'Vencidas' },
  { key: 'soon', label: 'Próximas' },
];

export default function AlertsAllView() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const all = useAppSelector(selectAttentionAll);
  const overdues = useAppSelector(selectOverdueAttention);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState<FilterKey>('all');

  useEffect(() => {
    if (all.length === 0) dispatch(fetchContracts());
  }, [dispatch, all.length]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await dispatch(fetchContracts());
    setRefreshing(false);
  };

  const items: AttentionItem[] = useMemo(() => {
    if (filter === 'overdue') return overdues;
    if (filter === 'soon') return all.filter((i) => i.urgency !== 'overdue');
    return all;
  }, [filter, all, overdues]);

  const total = useMemo(
    () => items.reduce((s, i) => s + i.installment.amount, 0),
    [items],
  );

  const openInstallment = (item: AttentionItem) =>
    navigate(
      `/app/contracts/${item.installment.contractId}/installments/${item.installment.id}`,
    );

  return (
    <Screen
      headerTitle="Em atenção"
      headerSubtitle={`${items.length} ${items.length === 1 ? 'parcela' : 'parcelas'}`}
      hasTabNavigation
      refreshLoading={refreshing}
      handleRefresh={handleRefresh}
    >
      <div className="flex flex-col gap-medium py-medium">
        <Box className="rounded-medium bg-intense-01 shadow-level1 p-medium flex items-center justify-between">
          <div>
            <Text className="text-captionLG color-intense-06 uppercase tracking-wide">
              Total exposto
            </Text>
            <Text className="mt-quark text-headingH1 color-complement-01">
              {formatCentsToBRLCompact(total)}
            </Text>
          </div>
          <div className="text-right">
            <Text className="text-captionMD color-intense-06">Vencidas</Text>
            <Text className="text-paragraphLG color-warning-06">
              {overdues.length}
            </Text>
          </div>
        </Box>

        <div className="flex items-center gap-quark">
          {FILTERS.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={
                  active
                    ? 'px-small py-smaller rounded-full bg-primary-01'
                    : 'px-small py-smaller rounded-full bg-intense-01 border border-intense-03 hover:bg-intense-02'
                }
              >
                <Text
                  className={
                    active
                      ? 'text-captionLG color-intense-01'
                      : 'text-captionLG color-intense-06'
                  }
                >
                  {f.label}
                </Text>
              </button>
            );
          })}
        </div>

        {items.length > 1 ? (
          <Button
            text="Dar baixa em várias parcelas"
            leftIcon="icon-grid"
            variant="ghost"
            className="border border-primary-01"
            onClick={() => navigate(`/app/alerts/bulk-pay?filter=${filter}`)}
            fullWidth
          />
        ) : null}

        {items.length === 0 ? (
          <EmptyState
            icon="icon-circle-check"
            title="Nada precisa de atenção"
            description="Você não tem parcelas vencidas nem com vencimento próximo nos próximos 30 dias."
          />
        ) : (
          <div className="flex flex-col gap-smaller">
            {items.map((item) => (
              <AttentionRow
                key={item.installment.id}
                item={item}
                onClick={() => openInstallment(item)}
              />
            ))}
          </div>
        )}
      </div>
    </Screen>
  );
}
